import PatientsAutocomplete from 'components/molecules/autocompleteFilter/patients';
import CcmInteractionStatusSelect from 'components/molecules/select/ccmInteractionStatus';
import CcmAgentDashboardAgencyDoctors from 'components/molecules/autocompleteFilter/ccmAgentDashboardAgencyDoctors';
import { useTranslation } from 'react-i18next';
import CcmStatusAgentDashboardSelect from 'components/molecules/select/ccmStatusAgentDashboard';
import ScheduledWithAutocomplete from 'components/molecules/autocompleteFilter/scheduledWith';
import CompletedByAutocomplete from 'components/molecules/autocompleteFilter/completedBy';
import useUser from 'utils/useUser';
import AgencyDoctorSelectBox from 'components/molecules/agency-doctor-select-box';
import { RoleGroups } from 'infrastructure/consts';

import NextAppointmentDateRange from './nextAppointmentDateRange';
import s from './styles.module.scss';

import type { ReactNode, FC } from 'react';
import type {
  Filters,
  AppendFilters,
} from 'infrastructure/hooks/ccmAgentDashboard/types';

interface Props {
  filters: Filters;
  appendFilters: AppendFilters;
}

const Wrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className={s['filter-wrapper']}>{children}</div>;
};

const Body: FC<Props> = ({ appendFilters, filters }) => {
  const { t } = useTranslation();
  const { isSupervisorRole } = useUser();

  return (
    <div className={s.wrapper}>
      <Wrapper>
        <PatientsAutocomplete
          value={filters.patientGuid}
          onChange={(e) => appendFilters({ patientGuid: e })}
        />
      </Wrapper>

      <Wrapper>
        {isSupervisorRole ? (
          <div className={s.input}>
            <label className={s.label}>{t('labels.ccmSpecialists')}</label>
            <AgencyDoctorSelectBox
              roles={RoleGroups.CCMSpecialistRoles}
              value={filters.ccmCaseManagerGuid || []}
              allowClear
              maxTagCount={0}
              mode="multiple"
              onChange={(_, e: any) =>
                appendFilters({
                  ccmCaseManagerGuid: e.map((el: any) => ({
                    label: el.label,
                    value: el.value,
                  })),
                })
              }
            />
          </div>
        ) : (
          <CcmAgentDashboardAgencyDoctors
            value={filters.ccmCaseManagerGuid || []}
            label={t('labels.ccmCaseManager')}
            onChange={(e) => appendFilters({ ccmCaseManagerGuid: e })}
          />
        )}
        <CcmAgentDashboardAgencyDoctors
          value={filters.ccmOnboardingAgentGuid || []}
          label={t('labels.ccmOnboardingAgent')}
          onChange={(e) => appendFilters({ ccmOnboardingAgentGuid: e })}
        />
      </Wrapper>
      <Wrapper>
        <ScheduledWithAutocomplete
          value={[
            ...(filters.nextAppointmentScheduledWithDoctors || []),
            ...(filters.nextAppointmentScheduledWithAgencyDoctors || []),
          ]}
          label={t('labels.nextAppointmentScheduledWith')}
          appendFilters={appendFilters}
        />
      </Wrapper>
      <Wrapper>
        <CcmInteractionStatusSelect
          value={filters.ccmInteractionStatus}
          onChange={(e) => appendFilters({ ccmInteractionStatus: e })}
        />
      </Wrapper>
      <Wrapper>
        <CcmStatusAgentDashboardSelect
          value={filters.ccmStatus}
          onChange={(e) => appendFilters({ ccmStatus: e })}
        />
        <NextAppointmentDateRange
          filters={filters}
          appendFilters={appendFilters}
        />
      </Wrapper>
      <Wrapper>
        <CompletedByAutocomplete
          value={[
            ...(filters.currentMonthAppointmentCompletedByDoctors || []),
            ...(filters.currentMonthAppointmentCompletedByAgencyDoctors || []),
          ]}
          label={t('labels.currentMonthAppointmentCompletedBy')}
          appendFilters={appendFilters}
        />
      </Wrapper>
    </div>
  );
};

export default Body;
